import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { GreenText } from 'components'
import { DateField } from 'components/fields'
import { DOTRejectionReasonTypeDto } from 'dtos/conformity/index'
import { dotRejectionValidationSchema, VehicleConformityDOTRejectionDto } from 'dtos/vehicles/conformity'
import { Form, Formik } from 'formik'

interface EditDOTRejectionDialogProps {
  dotRejectionReasonTypes: DOTRejectionReasonTypeDto[]
  initialValues?: VehicleConformityDOTRejectionDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: VehicleConformityDOTRejectionDto) => void
  open: boolean
}

export default function EditDOTRejectionDialog({ dotRejectionReasonTypes, initialValues, isLoading, onClose, onSave, open }: EditDOTRejectionDialogProps) {
  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues ?? {}}
        onSubmit={values => onSave(values)}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={dotRejectionValidationSchema}
      >
        {({ errors, handleBlur, handleChange, setFieldValue, touched, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Edit DOT Rejection</DialogTitle>

                  <Grid item xs={12}>
                    <DateField
                      disabled={isLoading}
                      error={Boolean(errors.dotRejectionDateTime)}
                      helperText={errors.dotRejectionDateTime}
                      label='Rejection Date'
                      onChange={value => setFieldValue('dotRejectionDateTime', value)}
                      value={values.dotRejectionDateTime}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.dotRejectionReasonType)}
                      fullWidth
                      helperText={errors.dotRejectionReasonType}
                      label='Rejection Reason'
                      name='dotRejectionReasonType'
                      onBlur={handleBlur}
                      onChange={e => setFieldValue('dotRejectionReasonType', JSON.parse(e.target.value))}
                      select
                      value={values.dotRejectionReasonType ? JSON.stringify(values.dotRejectionReasonType) : null}
                    >
                      {dotRejectionReasonTypes.map(dotRejectionReasonType => (
                        <MenuItem key={dotRejectionReasonType.dotRejectionReasonTypeId} value={JSON.stringify(dotRejectionReasonType)}>
                          {dotRejectionReasonType.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.dotRejectionNote)}
                      fullWidth
                      helperText={errors.dotRejectionNote}
                      label='Note'
                      name='dotRejectionNote'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='text'
                      value={values.dotRejectionNote ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='.pdf'
                      hidden
                      type='file'
                      id='uploadRejectionLetter'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) setFieldValue('dotRejectionLetterFile', e.target.files[0])
                      }}
                    />

                    <Button
                      disabled={isLoading}
                      onClick={() => {
                        document.getElementById('uploadRejectionLetter')?.click()
                      }}
                      variant='contained'
                    >
                      UPLOAD REJECTION LETTER
                    </Button>

                    <Typography color='#808080' display='inline' variant='body2' sx={{ ml: 1 }}>
                      {values.dotRejectionLetterFile?.name ?? values.dotRejectionLetterFileName ?? 'No file uploaded'}
                    </Typography>

                    {errors.dotRejectionLetterFile && (
                      <Typography color='error' variant='body2'>
                        {errors.dotRejectionLetterFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.needsToBeSentBackToCanada)}
                      fullWidth
                      helperText={errors.needsToBeSentBackToCanada}
                      label='Needs To Be Sent Back To Canada'
                      name='needsToBeSentBackToCanada'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      select
                      value={values.needsToBeSentBackToCanada ?? ''}
                    >
                      <MenuItem value='true'>Yes</MenuItem>

                      <MenuItem value='false'>No</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Rejection Date Field:**

A date-only field for entering the DOT Rejection date.

**Rejection Reason Drop Down Field:**

A dropdown menu containing the following rejection reasons:
 
   - Non-Compliance,
   - Open Recall,
   - Over 120 Days,
   - Missing Info,
   - Other

Users may select only one rejection reason.

**Note Field:**

A free-text field for entering additional information regarding the DOT Rejection.

Required if "Other" is selected as the rejection reason.

**Upload Rejection Letting Button:**

Clicking this button opens the user's File Explorer to upload the DOT Rejection Letter.

If a rejection PDF is uploaded and the user saves, the “Mark As Sent To DOT” date will be removed from the Vehicle Details – Conformity (tab) under “Conformity Details”.

Clicking the file name will download the uploaded Rejection Letter to the user's machine.

Only PDF files are allowed, with a maximum file size of 100 MB.

**Needs to be Sent Back To Canada Dropdown Field:**

A dropdown menu with the following options:

   - Yes
   - No

If "Yes" is selected:

   - The vehicle is no longer eligible for conformity.
   - It will no longer appear under the Conformity feature.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
